import { useQueryClient } from "@tanstack/vue-query";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import type { OrbState } from "@/shared/agent-display/BenOrb.vue";
import type { PendingAudio, PendingFile, PendingImage } from "../api/attachments";
import { attachToLiveTurn, queueTurnMessage, stopTurn } from "../api/live-turn";
import { forgetPendingTurn, readPendingTurn, rememberPendingTurn } from "../api/pending-turn";
import {
	type NavigateTarget,
	type PreparedDraft,
	type StreamHandlers,
	type StreamInput,
	type StreamOutcome,
	streamTurn,
} from "../api/stream";
import { summariseToolInput } from "../api/tool-labels";
import { addAttachment, type TurnAttachment } from "../api/turn-attachments";
import { useAgentsI18n } from "../i18n/use-agents-i18n";
import { assistantBubble, type Bubble, type LiveTool, rememberedBubble } from "./bubble";
import {
	type ChatDocument,
	changedDocumentPath,
	DOCUMENT_LIMIT,
	writingDocument,
} from "./chat-document";
import { useLiveEdits } from "./use-live-edits";
import { usePageContext } from "./use-page-context";
import { useTurnQueue } from "./use-turn-queue";
import { useTurnRecovery } from "./use-turn-recovery";

/**
 * Um turno acontecendo, e a thread que ele deixa atrás.
 *
 * Saiu de `StreamingChat.vue` (812 linhas, teto de 200): aqui está o TURNO, lá
 * ficou a TELA. As três regras herdadas da V2 seguem intactas — a resposta é a
 * última corrida de texto, silêncio é um turno válido, e um turno que escreve
 * deixa o cache inteiro velho.
 *
 * A FORMA da fala mora em `bubble.ts`, ao lado: este arquivo passou do teto de um
 * composable e o que dava para separar era justamente o que não é o fio.
 */

/** Quem declara o destino é o `stream.ts`, porque ele é contrato de fio.
 *  Reexportado para quem desenha a conversa não ter que saber disso. */
export type { NavigateTarget, PreparedDraft } from "../api/stream";
/** Idem para a forma da fala: quem desenha a conversa continua pedindo daqui. */
export type { Bubble, LiveTool } from "./bubble";

/**
 * QUANTO SILÊNCIO É MORTE.
 *
 * O servidor bate de 15 em 15 s enquanto o turno corre (`turn-sse.ts`), então
 * silêncio acima de três batimentos não se explica por atraso de rede — explica-se
 * por um fio que já não existe. É este número que torna a pergunta respondível:
 * antes do batimento, um turno a pensar e um fio morto eram indistinguíveis.
 */
const WIRE_SILENCE_MS = 45_000;

/**
 * Tools que só LEEM; qualquer outra escreve e faz o cache expirar no fim do turno.
 * Ao contrário de listar as que escrevem: `change_system` e `automation` ficaram de fora
 * daquela lista e o turno que só as usava terminava sem a tela reler nada.
 */
const READING_TOOL = /^(read_|query|manual$|web$|embeddables$)/;

/**
 * A tool cujo resultado a TELA DO SITE espera — o rascunho mudou no servidor.
 *
 * Separada de `READING_TOOL` porque a pergunta é outra: aquela decide o que
 * expira no FIM do turno, esta decide o que já não pode esperar pelo fim. O
 * construtor escreve a página secção a secção (`op:append`), e o dono está a
 * olhar para o preview enquanto isso: esperar pelo fim é ver a página aparecer
 * de uma vez, dez minutos depois de ela ter começado a existir.
 */
const LIVE_SITE_TOOL = "site";

export function useTurnStream(config: {
	agent: () => string;
	sessionId: () => string | null;
	conversationId: () => string | null;
	/** Uma tela que o servidor mandou abrir. Quem navega é quem montou o chat. */
	onNavigate: (target: NavigateTarget) => void;
	/**
	 * O servidor disse em que conversa este turno ficou GRAVADO.
	 *
	 * Ele abre a thread quando o cliente não trouxe nenhuma, e é assim que a tela
	 * fica a apontar para ela. Sem isto, um turno arquivado pelo servidor seria
	 * invisível na janela que o produziu — que é o defeito relatado a 24/08.
	 */
	onSession?: (id: string) => void;
	/**
	 * O BEN PREPAROU — o rascunho vai para o compositor do operador.
	 *
	 * Opcional porque só uma das três montagens de chat tem compositor de conversa
	 * ao lado: no OrbeDock e na tela de agentes não há para onde mandar, e o
	 * servidor nem sequer oferece a ferramenta ali (o menu do turno é decidido pelo
	 * porta, `domain/conversation-door.ts`).
	 */
	onPreparedDraft?: (draft: PreparedDraft) => void;
	/** Chegou coisa nova; a rolagem decide sozinha se segue. */
	onFrame: () => void;
}) {
	const queryClient = useQueryClient();
	/**
	 * Onde a pessoa está, em todo turno. Aqui e não na doca porque é aqui que o
	 * pedido é montado, e três superfícies mandam turno. O ganho é o `recordId`: o
	 * servidor já o usava para escopar a memória ao registro e nenhum lugar do
	 * front o mandava — "o que é isto?" numa ficha custava uma tool call de
	 * descoberta para reencontrar o que a barra de endereço já dizia.
	 */
	const { page, destinations } = usePageContext();
	const bubbles = ref<Bubble[]>([]);
	const streaming = ref(false);
	const liveText = ref("");
	const liveThinking = ref("");
	const liveTools = ref<LiveTool[]>([]);
	/** A peça grande sendo escrita AGORA — ferramenta e tamanho até aqui. É o que
	 *  separa "trabalhando" de "travado" numa escrita de minutos (o odontograma,
	 *  01/09: oito minutos de cursor parado). Null fora de uma escrita.
	 *  `text` é a JANELA viva do JSON que o modelo escreve — só o fim, para a
	 *  tela mostrar o código a correr sem guardar um documento de 200 KB. */
	const savedDocuments = ref<Record<string, number>>({});
	const documentCalls = new Map<string, string>();
	const liveDocument = ref<ChatDocument | null>(null);
	const liveEdits = useLiveEdits(liveDocument);
	watch(
		streaming,
		(active) => {
			if (!active) liveEdits.reset();
			if (!active && liveDocument.value?.status === "writing")
				liveDocument.value = { ...liveDocument.value, status: "failed" };
		},
		{ flush: "sync" },
	);
	const liveWriting = ref<{ tool: string; chars: number; text: string } | null>(null);
	const failure = ref("");
	/** O que o servidor sugeriu perguntar em seguida (frame `done`). */
	const nextSuggestions = ref<string[]>([]);
	/** Uma batida curta depois do turno, para TERMINAR ser visível — sem ela o
	 *  `success` do orbe era código morto. Limpa também no envio seguinte. */
	const justFinished = ref(false);
	let controller: AbortController | null = null;
	let disposed = false;
	let finishedTimer: ReturnType<typeof setTimeout> | null = null;
	/** O último sinal de vida do fio — quadro ou batimento. 0 fora de um turno. */
	let lastBeat = 0;
	/** O corte foi NOSSO (um fio calado apanhado ao voltar à tela), e não de uma
	 *  pessoa a carregar em parar. A diferença decide se há recuperação. */
	let cut = false;
	/** Sobe a cada envio: um regresso que começou antes dele já não fala por ninguém. */
	let epoch = 0;
	/** O regresso em curso, partilhado por quem pedir outro ao mesmo tempo. */
	let resuming: Promise<void> | null = null;
	// A dropped wire is not a dead turn: the server may have finished and archived
	// the reply. This is who rereads the session before anything is declared.
	const { t } = useAgentsI18n();
	const recovery = useTurnRecovery({
		agent: config.agent,
		sessionId: config.sessionId,
		bubbles,
		liveText,
		failure,
		finished: markFinished,
		onMessages: (messages) => {
			for (const message of messages) {
				const pending = queue.acknowledge(message.id, true);
				if (pending && !bubbles.value.some((b) => b.id === message.id))
					bubbles.value.push(rememberedBubble(message));
			}
		},
	});

	const queue = useTurnQueue({
		streaming,
		lost: recovery.lost,
		post: (message) =>
			// Com arquivo, espera o turno acabar e segue como turno próprio: a leitura
			// (quadros, transcrição) leva segundos e o servidor não a faz na fila.
			config.sessionId() && !message.files?.length
				? queueTurnMessage(message.id, request(message.text, [], message.images, message.audio))
				: Promise.resolve({ accepted: false }),
		send,
	});
	const appliedMessages = new Set<string>();

	const state = computed<OrbState>(() => {
		if (failure.value) return "trouble";
		if (!streaming.value) return justFinished.value ? "success" : "idle";
		// Tool rodando ganha do texto: é o fato mais interessante e o único sem
		// limite de tempo. Escrever a peça conta como agir pelo mesmo motivo.
		if (liveWriting.value) return "acting";
		if (liveTools.value.some((t) => t.output === undefined)) return "acting";
		return liveText.value ? "typing" : "thinking";
	});

	/**
	 * Para o turno — no servidor também, que é onde ele agora vive.
	 *
	 * Abortar o `fetch` deixou de parar o turno: ele sobrevive a fechar a aba de
	 * propósito, e por isso passou a ser preciso DIZER que se quer parar. Sem esta
	 * chamada, o botão limpava o ecrã e deixava o turno a correr a gastar tokens.
	 */
	function stop(): void {
		void stopTurn(config.agent(), config.sessionId());
		controller?.abort();
		recovery.cancel();
		controller = null;
		streaming.value = false;
		if (liveText.value.trim()) {
			bubbles.value = [...bubbles.value, { role: "assistant", text: liveText.value }];
		}
		liveText.value = "";
		liveTools.value = [];
		liveWriting.value = null;
	}

	/** O corpo do turno. Fora do `send` só para o fluxo de lá caber na leitura. */
	function request(
		prompt: string,
		history: StreamInput["history"],
		images: readonly PendingImage[],
		audio?: PendingAudio,
		files: readonly PendingFile[] = [],
	): StreamInput {
		return {
			agent: config.agent(),
			prompt,
			sessionId: config.sessionId(),
			page: page.value,
			...(destinations.value.length ? { destinations: destinations.value } : {}),
			...(config.conversationId() ? { conversationId: config.conversationId() } : {}),
			history,
			...(images.length
				? { images: images.map((i) => ({ mediaType: i.mediaType, data: i.data })) }
				: {}),
			...(audio ? { audio: { mediaType: audio.mediaType, data: audio.data } } : {}),
			...(files.length ? { files: files.map((f) => ({ id: f.id })) } : {}),
		};
	}

	/**
	 * O CORPO DE UM TURNO, visto daqui.
	 *
	 * Um só, para PEDIR um turno e para VOLTAR a um que já corria: são o mesmo fio,
	 * com os mesmos quadros, e têm de deixar a tela no mesmo estado. Montá-lo duas
	 * vezes seriam dois sítios onde esquecer de limpar `liveTools` no fim.
	 *
	 * `asked` é a única diferença entre os dois casos. Quem PERGUNTA tem uma
	 * pergunta, acende o "a pensar" no ato — a pessoa acabou de carregar em enviar
	 * — e reconcilia relendo a sessão se o fio cair. Quem VOLTA não tem nenhuma:
	 * só ocupa a tela se algo chegar (senão cada tela que monta piscava um turno
	 * inexistente), e não reconcilia, porque voltar já É a reconciliação.
	 */
	async function follow(
		exec: (handlers: StreamHandlers, signal: AbortSignal) => Promise<StreamOutcome>,
		asked: string | null,
	): Promise<StreamOutcome> {
		const question = asked !== null ? bubbles.value[bubbles.value.length - 1] : undefined;
		let showing = false;
		/** O instante em que este turno passa a ocupar a tela. */
		const begin = (): void => {
			if (showing) return;
			showing = true;
			liveDocument.value = null;
			liveEdits.reset();
			liveText.value = "";
			liveThinking.value = "";
			liveTools.value = [];
			liveWriting.value = null;
			justFinished.value = false;
			streaming.value = true;
			config.onFrame();
		};
		if (asked !== null) begin();

		const mine = new AbortController();
		controller = mine;
		// Limpo a cada turno: um corte que já foi julgado não pode pesar no seguinte.
		cut = false;
		let wrote = false;
		// O que o Ben grava aparece nas outras telas DURANTE o turno, não só no fim:
		// no máximo uma releitura a cada 2 s, e só das consultas que estão na tela.
		let refreshing: ReturnType<typeof setTimeout> | null = null;
		const refreshSoon = () => {
			if (refreshing) return;
			refreshing = setTimeout(() => {
				refreshing = null;
				void queryClient.invalidateQueries();
			}, 2000);
		};
		const went: NavigateTarget[] = [];
		/** Os arquivos entregues durante o turno. Ficam na fala quando ele termina. */
		let handed: TurnAttachment[] = [];

		const handlers: StreamHandlers = {
			onBeat: () => {
				lastBeat = Date.now();
				recovery.notice.value = "";
			},
			onQueued: (message, applied) => {
				const pending = queue.acknowledge(message.id, applied);
				if (!applied || appliedMessages.has(message.id)) return;
				appliedMessages.add(message.id);
				// O resultado de um subagente: entra com a origem dele, e não como fala da pessoa.
				if (message.origin === "agent") {
					if (!bubbles.value.some((b) => b.id === message.id))
						bubbles.value.push({
							id: message.id,
							role: "agent",
							text: message.text,
							at: Date.now(),
						});
				} else if (pending)
					bubbles.value.push({
						role: "user",
						text: message.text,
						at: Date.now(),
						...(pending.images.length ? { images: pending.images.map((i) => i.previewUrl) } : {}),
						...(pending.audio ? { audio: pending.audio.previewUrl } : {}),
					});
				config.onFrame();
			},
			onSession: (id) => {
				config.onSession?.(id);
				void queue.flush();
				// O servidor abriu a thread. O registo pendente tem de saber onde ela é,
				// ou um regresso depois de a aba ser descartada não teria onde procurar.
				if (asked !== null)
					rememberPendingTurn({ agent: config.agent(), sessionId: id, prompt: asked });
			},
			onPrompt: (text) => {
				if (question?.role === "user" && text.trim()) question.text = text;
			},
			onDelta: (text) => {
				begin();
				liveText.value += text;
				config.onFrame();
			},
			onThinking: (text) => {
				begin();
				liveThinking.value += text;
			},
			// Navega AGORA e fica guardado na thread: o cartão é o que resta da ação
			// depois que a rota mudou, e a doca vive fora do <RouterView>.
			onNavigate: (target) => {
				begin();
				went.push(target);
				config.onNavigate(target);
			},
			// Um arquivo em mãos. Guardado e não desenhado na hora: ele aparece com a
			// fala inteira, que é onde a pessoa lê o que ele é e por que ele veio.
			// O rascunho preparado. Não entra na fala: ele não é o que o Ben DISSE, é o
			// que ele pôs na caixa de outra pessoa — e o lugar onde isso se vê é a
			// caixa, não a conversa.
			onPreparedDraft: (draft) => config.onPreparedDraft?.(draft),
			onAttachment: (file) => {
				handed = addAttachment(handed, file);
			},
			onWriting: (progress) => {
				begin();
				// Os quadros trazem só o sufixo: a janela é o que já havia mais ele,
				// documentos conservam até 2 MiB; a atividade técnica conserva o fim.
				const accumulated = `${liveWriting.value?.text ?? ""}${progress.text ?? ""}`;
				const text =
					progress.tool === "files"
						? accumulated.slice(0, DOCUMENT_LIMIT + 1)
						: accumulated.slice(-4000);
				const document = writingDocument(progress.tool, text);
				if (document) liveDocument.value = document;
				else void liveEdits.update(progress.tool, text);
				liveWriting.value = { tool: progress.tool, chars: progress.chars, text };
			},
			onToolCall: (call) => {
				begin();
				const path = changedDocumentPath(call.name, call.input);
				if (path) documentCalls.set(call.id, path);
				const document = writingDocument(call.name, call.input);
				if (document) liveDocument.value = { ...document, callId: call.id };
				else void liveEdits.update(call.name, call.input, call.id);
				// O bloco fechou: a escrita virou chamada, e o cartão dela assume.
				liveWriting.value = null;
				if (!READING_TOOL.test(call.name)) wrote = true;
				const tool = { id: call.id, name: call.name, input: summariseToolInput(call.input, t) };
				liveTools.value = [...liveTools.value, { ...tool, isError: false }];
			},
			onToolTasks: ({ id, tasks }) => {
				liveTools.value = liveTools.value.map((t) => (t.id === id ? { ...t, tasks } : t));
			},
			onToolResult: (result) => {
				const path = documentCalls.get(result.id);
				documentCalls.delete(result.id);
				if (path) liveEdits.reset();
				if (path && !result.isError)
					savedDocuments.value = {
						...savedDocuments.value,
						[path]: (savedDocuments.value[path] ?? 0) + 1,
					};
				if (liveDocument.value?.callId === result.id) {
					liveDocument.value = {
						...liveDocument.value,
						status: result.isError ? "failed" : "ready",
					};
				}
				const called = liveTools.value.find((t) => t.id === result.id);
				liveTools.value = liveTools.value.map((t) =>
					t.id === result.id
						? { ...t, output: result.output, isError: result.isError, ms: result.ms }
						: t,
				);
				// O rascunho do site JÁ mudou no servidor: a lista de versões relê-se
				// agora, e o preview segue-a pelo `updatedAt` que ela traz.
				if (called?.name === LIVE_SITE_TOOL && !result.isError) {
					void queryClient.invalidateQueries({ queryKey: ["site"] });
				} else if (called && !result.isError && !READING_TOOL.test(called.name)) refreshSoon();
			},
			onDone: (done) => {
				begin();
				// Turno SILENCIOSO não ganha o tique: decidir não responder não é um feito.
				if (!done.silent && !done.refused) markFinished();
				// Quatro é o teto do que cabe embaixo da resposta sem virar um menu.
				nextSuggestions.value = (done.next ?? []).filter((s) => s.trim() !== "").slice(0, 4);
				// Recusada, a PERGUNTA também é marcada: é ela que envenenava a conversa.
				const marked = done.refused ? markLastQuestionRefused(bubbles.value) : bubbles.value;
				// A chave guardada no cofre sai do que está na tela e do que se reenvia.
				const asked = scrubbed(marked, done.scrub ?? []);
				bubbles.value = [...asked, assistantBubble(done, went, handed)];
			},
			onError: (message) => {
				begin();
				failure.value = message;
				bubbles.value = [...bubbles.value, { role: "assistant", text: message, failed: true }];
			},
		};

		/**
		 * VOLTAR A LIGAR-SE A ESTE MESMO TURNO, pelos mesmos ouvintes.
		 *
		 * O servidor guarda tudo o que já disse e repete-o desde o INÍCIO
		 * (`in-process-live-turns.ts`), então o que está no ecrã tem de sair antes —
		 * ou o texto que a pessoa acabou de ler aparece somado a si próprio.
		 */
		const reattach = (signal: AbortSignal): Promise<StreamOutcome> => {
			liveText.value = "";
			liveThinking.value = "";
			liveTools.value = [];
			liveWriting.value = null;
			went.length = 0;
			handed = [];
			return attachToLiveTurn(config.agent(), handlers, signal, t, config.sessionId());
		};

		lastBeat = Date.now();
		const outcome = await exec(handlers, mine.signal);
		if (disposed) return "aborted";

		// Um corte NOSSO é uma queda, e não uma pessoa a desistir: quem desiste
		// carrega no botão, e isso passa por `stop`. Sem esta distinção, cortar um fio
		// morto ao voltar à tela seria indistinguível de parar — e não recuperaria nada.
		const dropped = outcome === "dropped" || (outcome === "aborted" && cut);
		// Largado ANTES da recuperação: este cancelamento é o do fio que já morreu, e
		// deixá-lo à mão daria a quem voltasse à tela um travão que não trava nada.
		// Quem cancela a BUSCA é `recovery.cancel`, e `stop` chama-o. Só se ainda for
		// o NOSSO: um regresso cancelado por um envio acaba depois de o envio ter
		// posto o dele, e apagá-lo deixaria o turno novo sem travão.
		if (controller === mine) {
			cut = false;
			lastBeat = 0;
			controller = null;
		}

		// A conexão que caiu não é veredito: reata o turno em voo, e só se não houver
		// nenhum é que relê a sessão. Ver `recover-turn.ts` para a ordem e o porquê.
		if (dropped && asked !== null) await recovery.afterDrop(asked, reattach);
		if (outcome === "done" || outcome === "error")
			forgetPendingTurn(config.agent(), config.sessionId());
		// Nada chegou — não havia turno nenhum a que voltar. A tela nunca soube
		// deste, e limpá-la agora apagaria o que ela estava a mostrar.
		if (!showing) return outcome;

		streaming.value = false;
		liveText.value = "";
		liveTools.value = [];
		liveWriting.value = null;
		// O construtor cria telas, funis e regras: invalidar só "agents" deixaria a
		// navegação mostrando estrutura que não existe mais.
		if (wrote) void queryClient.invalidateQueries();
		// A lista de conversas: todo turno grava a pergunta, e o primeiro dá o título.
		// Relida só quando o turno escrevia, a conversa de leitura ficava
		// "Sem título · 0 mensagens" na lista (27/09).
		else void queryClient.invalidateQueries({ queryKey: ["agent-sessions"] });
		config.onFrame();
		return outcome;
	}

	async function send(
		prompt: string,
		images: readonly PendingImage[] = [],
		audio?: PendingAudio,
		files: readonly PendingFile[] = [],
	): Promise<void> {
		if ((!prompt && images.length === 0 && !audio && files.length === 0) || streaming.value) return;
		// Um regresso ainda à procura (a busca de um turno caído, ou o `attach` do
		// `resume`) é outro ouvinte do MESMO turno: reataria o turno novo pelos
		// ouvintes dele, e cada ferramenta, pensamento e resposta chegava em dobro.
		// Sai antes de o turno novo existir no servidor.
		epoch += 1;
		recovery.cancel();
		controller?.abort();
		failure.value = "";
		recovery.notice.value = "";
		recovery.lost.value = false;
		nextSuggestions.value = [];

		// A voz ainda não tem palavras deste lado — quem as escreve é o servidor,
		// transcrevendo. A bolha local mostra o gesto ("🎙️ …"); depois de um F5 a
		// sessão devolve o transcrito, que é a versão boa.
		const shown = prompt || (audio ? t("chat.voiceNote") : t("chat.whatIsThis"));
		bubbles.value = [
			...bubbles.value,
			{
				role: "user",
				text: shown,
				// A hora da PERGUNTA. A barra sob a fala da pessoa mostra-a como a V2
				// mostra, e ela só existia do lado do assistente — metade da conversa
				// tinha relógio e a outra metade não.
				at: Date.now(),
				...(images.length ? { images: images.map((i) => i.previewUrl) } : {}),
				...(audio ? { audio: audio.previewUrl } : {}),
				// Os arquivos, como a bolha de um F5 os mostra: já estão guardados.
				...(files.length
					? {
							attachments: files.map((f) => ({
								id: f.id,
								name: f.name,
								mime: f.mime,
								sizeBytes: f.sizeBytes,
							})),
						}
					: {}),
			},
		];

		// Os turnos anteriores saem do que está na tela: o servidor também os tem, e
		// uma ida e volta aqui atrasaria o primeiro token.
		const history = bubbles.value
			.slice(0, -1)
			.filter((b) => !b.silent && !b.refused)
			.slice(-20)
			// O resultado de um subagente chega ao modelo como entrada, igual ao servidor.
			.map((b) => ({ role: b.role === "assistant" ? b.role : ("user" as const), content: b.text }));

		// Imagem sem palavra nenhuma é uma mensagem de verdade — "o que é isto?"
		// está implícito. Com ÁUDIO o prompt viaja como está (vazio inclusive):
		// completá-lo aqui viraria texto digitado na frente da transcrição.
		const asked = prompt || (audio ? "" : t("chat.whatIsThis"));
		// ANTES do fio, porque é o fio que pode não voltar. O Safari do iOS suspende
		// — e sob pressão de memória descarta — uma página em segundo plano, e tudo o
		// que este `composable` sabe morre com ela. Isto é o que sobrevive.
		rememberPendingTurn({ agent: config.agent(), sessionId: config.sessionId(), prompt: shown });
		await follow(
			(handlers, signal) =>
				streamTurn(request(asked, history, images, audio, files), handlers, signal, t),
			shown,
		);
	}

	/**
	 * VOLTA a um turno que ficou a correr enquanto ninguém via.
	 *
	 * Os dois momentos em que alguém pode ter saído a meio são: esta tela montar
	 * (recarregou, mudou de rota, reabriu o telemóvel) e a aba voltar a ficar
	 * visível (trocou de app). Nos dois, o servidor repete o que já foi dito e
	 * segue dali — e quando não há turno nenhum, responde `none` e nada acontece.
	 *
	 * Quem chama no momento do MONTE é a tela, e não o `onMounted` daqui: as falas
	 * da sessão chegam por um pedido à parte, e voltar a um turno antes delas
	 * poria a resposta numa thread que ainda estava vazia.
	 */
	function resume(): Promise<void> {
		// UM regresso de cada vez. Montar, voltar à aba e `pageshow` chegam juntos,
		// e dois regressos eram duas buscas a escrever a mesma linha e a mesma thread:
		// uma apagava o aviso da outra, e "ver o turno" sumia a meio.
		resuming ??= look().finally(() => {
			resuming = null;
		});
		return resuming;
	}

	async function look(): Promise<void> {
		if (disposed || streaming.value || !config.sessionId()) return;
		const asOf = epoch;
		const outcome = await follow(
			(handlers, signal) =>
				attachToLiveTurn(config.agent(), handlers, signal, t, config.sessionId()),
			null,
		);
		if (outcome === "done" || outcome === "error") {
			forgetPendingTurn(config.agent(), config.sessionId());
			return;
		}
		// Um envio cortou este regresso: o pendente agora é o do turno NOVO, e
		// procurá-lo aqui seria o segundo ouvinte que o envio acabou de calar.
		if (asOf !== epoch) return;
		await recoverPending();
	}

	/**
	 * NÃO HAVIA TURNO EM VOO — mas pode ter havido um enquanto ninguém via.
	 *
	 * Este é o caso que o dono mediu no iPhone: sair da tela, o Safari suspende a
	 * página e fecha as ligações, o turno acaba no servidor, e ao voltar não há
	 * nada a que se ligar. A resposta existe, arquivada; o que faltava era alguém
	 * saber que a devia procurar. O registo pendente é essa memória — e é a única
	 * que sobrevive à aba ser descartada e recarregada.
	 */
	async function recoverPending(): Promise<void> {
		const pending = readPendingTurn(config.agent(), config.sessionId());
		if (!pending) return;
		/**
		 * E TEM DE SER ESTA CONVERSA.
		 *
		 * Duas threads do mesmo agente: perguntou-se na A, saiu-se do telemóvel, e ao
		 * voltar a tela está na B. Sem esta linha, a busca leria a sessão A e
		 * desenharia a resposta dela na thread B — a resposta certa na conversa
		 * errada, que é pior do que não a mostrar. O registo fica onde está: quem
		 * abrir a A volta a encontrá-lo.
		 */
		if (pending.sessionId && pending.sessionId !== config.sessionId()) return;
		// A thread recarregada já pode estar a mostrar a resposta: a tela lê as falas
		// da sessão antes de chamar `resume`. Procurá-la outra vez desenhá-la-ia duas
		// vezes — o defeito que fez a carência de dois minutos sair do servidor.
		if (answered(pending.prompt)) {
			forgetPendingTurn(config.agent(), config.sessionId());
			return;
		}
		await follow(async (handlers, signal) => {
			await recovery.afterDrop(pending.prompt, (retry) =>
				attachToLiveTurn(config.agent(), handlers, retry, t, pending.sessionId),
			);
			// "none" e nunca "done": quem apaga o registo pendente é a busca, e só
			// quando ela ACHA. Dizer "acabou" aqui apagá-lo-ia mesmo quando ela
			// desistiu — e "ver o turno" ficaria sem a âncora por onde procurar.
			return signal.aborted ? "aborted" : "none";
		}, null);
	}

	/**
	 * A thread já mostra uma resposta a esta pergunta?
	 *
	 * A ÚLTIMA vez que ela foi feita é a que conta: "regenerar" guarda toda
	 * tentativa. E a fala de FALHA não conta como resposta — ela é uma fala do
	 * assistente na forma, mas dizer "a conexão caiu" é o contrário de responder, e
	 * contá-la faria "ver o turno" apagar a própria âncora que ele usa para procurar.
	 */
	function answered(prompt: string): boolean {
		const thread = bubbles.value;
		for (let i = thread.length - 1; i >= 0; i--) {
			const bubble = thread[i];
			if (bubble?.role !== "user" || bubble.text !== prompt) continue;
			return thread.slice(i + 1).some((b) => b.role === "assistant" && !b.failed);
		}
		return false;
	}

	function markFinished(): void {
		if (finishedTimer) clearTimeout(finishedTimer);
		justFinished.value = true;
		finishedTimer = setTimeout(() => {
			justFinished.value = false;
		}, 1600);
	}

	/**
	 * Voltar da troca de app — o caso do telemóvel, que é onde isto mais dói.
	 *
	 * ── O FIO QUE MORREU SEM O DIZER ──────────────────────────────────────
	 *
	 * O Safari do iOS fecha as ligações de uma página suspensa, e nem sempre o
	 * `fetch` deste lado chega a rejeitar: a promessa fica pendurada, o `follow`
	 * nunca acaba, `streaming` nunca desliga e a recuperação nunca corre. Era esse o
	 * beco — e o batimento do servidor é o que agora deixa vê-lo, porque um fio vivo
	 * fala de 15 em 15 s. Cortar aqui é o que faz o `follow` chegar à parte que reata.
	 */
	function onVisible(): void {
		if (document.visibilityState !== "visible") return;
		if (streaming.value) {
			if (lastBeat > 0 && Date.now() - lastBeat > WIRE_SILENCE_MS) {
				cut = true;
				controller?.abort();
			}
			// O turno que está a correr trata de si: `resume` recusaria de qualquer modo.
			return;
		}
		void resume();
	}

	/**
	 * Uma aba restaurada do cache de navegação não dispara `visibilitychange` —
	 * ela nunca ficou escondida, do ponto de vista dela: foi congelada e
	 * descongelada. `pageshow` é o evento que essa travessia dispara, e no iOS ela
	 * é comum de mais para ficar de fora.
	 */
	function onShow(): void {
		void resume();
	}

	onMounted(() => {
		document.addEventListener("visibilitychange", onVisible);
		window.addEventListener("pageshow", onShow);
	});

	/**
	 * Sair fecha a JANELA, não o turno.
	 *
	 * `controller.abort()` continua aqui e continua certo — ele para de ler o fio,
	 * e ler um fio depois de a tela morrer não serve a ninguém. O que mudou é o
	 * outro lado: o servidor deixou de tomar isto por uma ordem para desistir.
	 */
	onBeforeUnmount(() => {
		disposed = true;
		document.removeEventListener("visibilitychange", onVisible);
		window.removeEventListener("pageshow", onShow);
		controller?.abort();
		recovery.cancel();
		queue.dispose();
		if (finishedTimer) clearTimeout(finishedTimer);
	});

	return {
		queued: queue.queued,
		enqueue: queue.enqueue,
		/** Volta a um turno em voo. A tela chama-o depois de carregar a thread. */
		resume,
		bubbles,
		streaming,
		liveText,
		liveThinking,
		liveTools,
		liveWriting,
		liveDocument,
		savedDocuments,
		failure,
		/** A linha discreta da reconciliação — narração, nunca alarme. */
		notice: recovery.notice,
		/** A busca esgotou-se. A tela oferece olhar outra vez, e nunca reenviar. */
		lost: recovery.lost,
		/** "Ver o turno": procura de novo, sem gastar um turno novo. */
		lookAgain: resume,
		nextSuggestions,
		state,
		send,
		stop,
	};
}

/** A última pergunta da pessoa, marcada como recusada pelo provedor. */
function markLastQuestionRefused(bubbles: readonly Bubble[]): Bubble[] {
	const at = bubbles.map((b) => b.role).lastIndexOf("user");
	return bubbles.map((b, i) => (i === at ? { ...b, refused: true } : b));
}

/** Troca cada chave guardada pelo nome dela em todas as bolhas — ver `StreamDone.scrub`. */
export function scrubbed<T extends { text: string }>(
	bubbles: readonly T[],
	pairs: ReadonlyArray<{ from: string; to: string }>,
): T[] {
	if (pairs.length === 0) return [...bubbles];
	return bubbles.map((b) => {
		const text = pairs.reduce((out, p) => out.split(p.from).join(p.to), b.text);
		return text === b.text ? b : { ...b, text };
	});
}
